/**
 * Role utilities for routing and UI gating.
 *
 * Canto roles (as returned by /me):
 *   Bartender, BarManager, Brewer, Leader, Payroll, Admin, BarKiosk
 *
 * A user can hold multiple roles. getPrimaryRole() determines which home
 * screen to render by returning the highest-priority role. Admins layer on
 * top of another role's home rather than replacing it — they see the home
 * for their next-highest role, plus an admin entry point in the header.
 *
 * BarKiosk is highest priority so kiosk accounts always land in KioskPage
 * regardless of any other roles.
 */

export type CantoRole =
  | 'Bartender'
  | 'BarManager'
  | 'Brewer'
  | 'Leader'
  | 'Payroll'
  | 'Admin'
  | 'BarKiosk';

// BarKiosk first — kiosk accounts render KioskPage for any path.
// Admin is not a standalone home — fall through to the next role.
const HOME_PRIORITY: CantoRole[] = ['BarKiosk', 'Leader', 'Payroll', 'Brewer', 'BarManager', 'Bartender'];

/**
 * Returns the role that determines which home screen to show.
 * Returns null if the user has no recognized role.
 */
export function getPrimaryRole(roles: string[]): CantoRole | null {
  const roleSet = new Set(roles);
  for (const r of HOME_PRIORITY) {
    if (roleSet.has(r)) return r;
  }
  return null;
}

/** True if this role can see and manage a specific location. */
export function isLocationScoped(roles: string[]): boolean {
  const roleSet = new Set(roles);
  return !roleSet.has('Leader') && !roleSet.has('Admin') && !roleSet.has('Payroll');
}

/** True if the user can access all locations regardless of group membership. */
export function hasFullLocationAccess(roles: string[]): boolean {
  return roles.includes('Leader') || roles.includes('Admin') || roles.includes('Payroll');
}

/** Roles that can create/edit tasks. */
export const TASK_MANAGE_ROLES: CantoRole[] = ['BarManager', 'Leader', 'Admin'];

/** Roles that can submit/manage supply requests. */
export const SUPPLY_WRITE_ROLES: CantoRole[] = ['Bartender', 'BarManager', 'Leader', 'Admin'];

/** Roles with Payroll access. */
export const PAYROLL_ROLES: CantoRole[] = ['Payroll', 'Admin'];

/** Roles that render the kiosk UI. */
export const KIOSK_ROLES: CantoRole[] = ['BarKiosk'];

/** Roles that can view the Timecards tab. */
export const TIMECARD_ROLES: CantoRole[] = ['Payroll', 'Admin'];

/** Roles that can view and read/write the beer catalog. */
export const BEER_CATALOG_ROLES: CantoRole[] = ['BarManager', 'Brewer', 'Leader', 'Admin'];
